import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Alert,
  Link,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Chip,
  Checkbox,
  Tooltip,
  Typography,
  CircularProgress,
  OutlinedInput,
  ListItemText,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { useApiClient } from '../../config/axios';
import { useCustomer } from '../../contexts/CustomerContext';
import VariantPickerModal from './VariantPickerModal';
import type { VariantChoice } from './VariantPickerModal';

interface CoverageMatrixProps {
  section: string;            // top_nav (required)
  leftNav?: string | null;    // narrows views to this leftNav folder
  viewLabel?: string | null;  // narrows further to this viewLabel folder under leftNav
  onSavedRefresh?: () => void;
  onClose?: () => void;
}

interface PrdSummary {
  import_id: number;
  file_name: string;
  imported_at: string;
  in_scope_metric_count: number;
  in_scope_attribute_count: number;
}

interface MatrixView {
  view_key: string;
  view_name: string;
  view_label: string;
  view_type: string;
  updated_at: string;
  leftNav: string;
  configName: string;
}

interface PresenceEntry {
  present: boolean;
  renderer?: string;
}

interface MatrixRow {
  data_index: string;
  item_type: string | null;
  label: string | null;
  category: string | null;
  prd_calculation: string | null;
  in_prd_scope: boolean | null;
  is_attribute: boolean;
  current_renderer: string | null;
  needs_variant: boolean;
  presence: { [view_key: string]: PresenceEntry };
}

interface MatrixData {
  section: string;
  prd_summary: PrdSummary | null;
  views: MatrixView[];
  rows: MatrixRow[];
}

type TypeFilter = 'all' | 'metric' | 'attribute';

const diffKey = (dataIndex: string, viewKey: string) => `${dataIndex}|${viewKey}`;

const formatImportedDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
};

const CoverageMatrix: React.FC<CoverageMatrixProps> = ({ section, leftNav, viewLabel, onSavedRefresh, onClose }) => {
  const apiClient = useApiClient();
  const { currentCustomer } = useCustomer();

  const [data, setData] = useState<MatrixData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [diff, setDiff] = useState<Map<string, boolean>>(new Map());

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [searchColumn, setSearchColumn] = useState<'all' | 'data_index' | 'label' | 'renderer'>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [prdScopeOnly, setPrdScopeOnly] = useState(true);

  const [pickerQueue, setPickerQueue] = useState<
    { dataIndex: string; prdCalculation: string | null }[]
  >([]);
  const [currentVariantChoices, setCurrentVariantChoices] = useState<
    Record<string, VariantChoice>
  >({});
  const [conflictBanner, setConflictBanner] = useState<string | null>(null);

  // Pending-save flag — when true, finishing the picker queue triggers handleSaveAfterVariants
  const pendingSaveRef = useRef(false);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Fetch matrix
  const fetchMatrix = useCallback(async () => {
    if (!section) return;
    setLoading(true);
    try {
      const params: Record<string, string> = { section };
      if (leftNav) params.leftNav = leftNav;
      if (viewLabel) params.viewLabel = viewLabel;
      const { data: resp } = await apiClient.get('/coverage-matrix', { params });
      setData(resp as MatrixData);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error || err?.message || 'Failed to load coverage matrix';
      setConflictBanner(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiClient, section, leftNav, viewLabel]);

  // Refetch when ANY scope param changes (section, leftNav, viewLabel) or the
  // customer changes. fetchMatrix is the canonical dep — its useCallback already
  // closes over all four — so depending on it covers all of them.
  useEffect(() => {
    fetchMatrix();
  }, [fetchMatrix, currentCustomer?.tenant, currentCustomer?.environment]);

  // Default prdScopeOnly based on whether prd_summary exists.
  useEffect(() => {
    setPrdScopeOnly(Boolean(data?.prd_summary));
  }, [data?.prd_summary?.import_id]);

  // Distinct categories
  const categories = useMemo(() => {
    if (!data) return [] as string[];
    const set = new Set<string>();
    data.rows.forEach((r) => {
      if (r.category) set.add(r.category);
    });
    return Array.from(set).sort();
  }, [data]);

  const hasAnyCategory = categories.length > 0;

  // Effective presence after diff
  const effectivePresence = useCallback(
    (row: MatrixRow, viewKey: string): boolean => {
      const k = diffKey(row.data_index, viewKey);
      if (diff.has(k)) return diff.get(k) as boolean;
      return Boolean(row.presence?.[viewKey]?.present);
    },
    [diff]
  );

  // Filter rows
  const filteredRows = useMemo(() => {
    if (!data) return [] as MatrixRow[];
    const lcSearch = search.trim().toLowerCase();

    return data.rows.filter((row) => {
      // Search — scoped by `searchColumn` so the user can constrain matches
      // to a single column. 'all' ORs across data_index / label / renderer.
      if (lcSearch) {
        const di = (row.data_index || '').toLowerCase();
        const lbl = (row.label || '').toLowerCase();
        const rnd = (row.current_renderer || '').toLowerCase();
        let matched = false;
        if (searchColumn === 'data_index') matched = di.includes(lcSearch);
        else if (searchColumn === 'label') matched = lbl.includes(lcSearch);
        else if (searchColumn === 'renderer') matched = rnd.includes(lcSearch);
        else matched = di.includes(lcSearch) || lbl.includes(lcSearch) || rnd.includes(lcSearch);
        if (!matched) return false;
      }
      // Type filter
      if (typeFilter === 'metric' && row.is_attribute) return false;
      if (typeFilter === 'attribute' && !row.is_attribute) return false;
      // Category
      if (categoryFilter.length > 0) {
        if (!row.category || !categoryFilter.includes(row.category)) return false;
      }
      // PRD scope only — show ONLY rows that are in the latest PRD's in-scope set.
      // Toggle OFF shows everything (PRD-in-scope + currently referenced in this scope's views).
      // Pending adds (diff entries) for a non-in-scope row still pass so the planner
      // doesn't lose track of an in-progress add.
      if (prdScopeOnly && data.prd_summary) {
        const inScope = row.in_prd_scope === true;
        const hasPendingAdd = Array.from(diff.entries()).some(
          ([k, v]) => v === true && k.startsWith(row.data_index + '|')
        );
        if (!inScope && !hasPendingAdd) return false;
      }
      return true;
    });
  }, [data, search, searchColumn, typeFilter, categoryFilter, prdScopeOnly, diff]);

  // Diff stats
  const diffStats = useMemo(() => {
    let adds = 0;
    let removes = 0;
    if (!data) return { adds, removes };
    const rowsByDi = new Map(data.rows.map((r) => [r.data_index, r]));
    diff.forEach((desired, k) => {
      const [di, viewKey] = k.split('|');
      const row = rowsByDi.get(di);
      if (!row) return;
      const current = Boolean(row.presence?.[viewKey]?.present);
      if (desired && !current) adds += 1;
      else if (!desired && current) removes += 1;
    });
    return { adds, removes };
  }, [diff, data]);

  const diffEmpty = diffStats.adds === 0 && diffStats.removes === 0;

  // Toggle a checkbox: flip effective state. If the resulting desired state matches current, remove diff entry.
  const handleToggle = useCallback(
    (row: MatrixRow, viewKey: string) => {
      setDiff((prev) => {
        const next = new Map(prev);
        const k = diffKey(row.data_index, viewKey);
        const current = Boolean(row.presence?.[viewKey]?.present);
        const effective = next.has(k) ? (next.get(k) as boolean) : current;
        const desired = !effective;
        if (desired === current) {
          next.delete(k);
        } else {
          next.set(k, desired);
        }
        return next;
      });
    },
    []
  );

  // Reset diff
  const handleReset = () => {
    setDiff(new Map());
    setCurrentVariantChoices({});
    pendingSaveRef.current = false;
  };

  // Build adds/removes from current diff
  const buildAddsRemoves = useCallback(() => {
    const adds: { data_index: string; view_key: string }[] = [];
    const removes: { data_index: string; view_key: string }[] = [];
    if (!data) return { adds, removes };
    const rowsByDi = new Map(data.rows.map((r) => [r.data_index, r]));
    diff.forEach((desired, k) => {
      const [di, viewKey] = k.split('|');
      const row = rowsByDi.get(di);
      if (!row) return;
      const current = Boolean(row.presence?.[viewKey]?.present);
      if (desired && !current) adds.push({ data_index: di, view_key: viewKey });
      else if (!desired && current) removes.push({ data_index: di, view_key: viewKey });
    });
    return { adds, removes };
  }, [diff, data]);

  // Actually call the bulk-edit endpoint with the assembled body
  const submitBulkEdit = useCallback(
    async (variantChoices: Record<string, VariantChoice>) => {
      if (!data) return;
      const { adds, removes } = buildAddsRemoves();
      // optimistic_view_versions for any view touched by adds or removes
      const touchedViewKeys = new Set<string>();
      adds.forEach((a) => touchedViewKeys.add(a.view_key));
      removes.forEach((r) => touchedViewKeys.add(r.view_key));
      const optimistic_view_versions: Record<string, string> = {};
      data.views.forEach((v) => {
        if (touchedViewKeys.has(v.view_key)) {
          optimistic_view_versions[v.view_key] = v.updated_at;
        }
      });

      setSaving(true);
      try {
        await apiClient.post('/view-def-item/bulk-edit', {
          adds,
          removes,
          variant_choices: variantChoices,
          optimistic_view_versions,
        });
        // Success
        setDiff(new Map());
        setCurrentVariantChoices({});
        pendingSaveRef.current = false;
        await fetchMatrix();
        onSavedRefresh?.();
      } catch (err: any) {
        const status = err?.response?.status;
        const body = err?.response?.data;
        if (status === 409 && Array.isArray(body?.stale)) {
          // Refetch but preserve diff
          setConflictBanner(
            'These views were updated elsewhere — your edits were preserved, please review and save again.'
          );
          await fetchMatrix();
        } else if (status === 400 && Array.isArray(body?.needs_variant)) {
          // Enqueue the additional variant prompts and pause
          const rowsByDi = data.rows.reduce<Record<string, MatrixRow>>((acc, r) => {
            acc[r.data_index] = r;
            return acc;
          }, {});
          const additions = (body.needs_variant as string[])
            .filter((di) => !variantChoices[di])
            .map((di) => ({
              dataIndex: di,
              prdCalculation: rowsByDi[di]?.prd_calculation ?? null,
            }));
          if (additions.length > 0) {
            pendingSaveRef.current = true;
            setPickerQueue((prev) => [...prev, ...additions]);
          } else {
            setConflictBanner(
              body?.error || 'A variant choice is required but could not be resolved.'
            );
          }
        } else {
          setConflictBanner(body?.error || err?.message || 'Save failed');
        }
      } finally {
        setSaving(false);
      }
    },
    [apiClient, data, buildAddsRemoves, fetchMatrix, onSavedRefresh]
  );

  // Resume save after variant queue drains
  const handleSaveAfterVariants = useCallback(() => {
    pendingSaveRef.current = false;
    // Use the latest variant choices via a state-effect snapshot
    setCurrentVariantChoices((choices) => {
      // fire-and-forget; choices is the freshest state
      submitBulkEdit(choices);
      return choices;
    });
  }, [submitBulkEdit]);

  // Save flow — step 1 & 2
  const handleSave = useCallback(() => {
    if (!data) return;
    if (saving) return;
    const { adds } = buildAddsRemoves();
    if (adds.length === 0 && diffStats.removes === 0) return;

    // Find adds that need a variant and don't already have a choice
    const rowsByDi = new Map(data.rows.map((r) => [r.data_index, r]));
    const needVariantDis = new Set<string>();
    adds.forEach((a) => {
      const row = rowsByDi.get(a.data_index);
      if (row?.needs_variant && !currentVariantChoices[a.data_index]) {
        needVariantDis.add(a.data_index);
      }
    });

    if (needVariantDis.size > 0) {
      pendingSaveRef.current = true;
      const queue = Array.from(needVariantDis).map((di) => ({
        dataIndex: di,
        prdCalculation: rowsByDi.get(di)?.prd_calculation ?? null,
      }));
      setPickerQueue(queue);
      return;
    }

    submitBulkEdit(currentVariantChoices);
  }, [
    data,
    saving,
    buildAddsRemoves,
    diffStats.removes,
    currentVariantChoices,
    submitBulkEdit,
  ]);

  // Build columns
  const columns = useMemo<GridColDef<MatrixRow>[]>(() => {
    if (!data) return [];
    const cols: GridColDef<MatrixRow>[] = [];

    const showScopeCol = Boolean(data.prd_summary);

    if (showScopeCol) {
      cols.push({
        field: '__scope',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row;
          const anyPresent = Object.values(row.presence || {}).some(
            (p) => p?.present === true
          );
          if (row.in_prd_scope === true && anyPresent) {
            return (
              <Tooltip title="In PRD scope and present in at least one view">
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
              </Tooltip>
            );
          }
          if (row.in_prd_scope === true && !anyPresent) {
            return (
              <Tooltip title="In PRD scope, not yet added">
                <AddCircleIcon sx={{ color: 'info.main', fontSize: 18 }} />
              </Tooltip>
            );
          }
          if (row.in_prd_scope === false && anyPresent) {
            return (
              <Tooltip title="Out of PRD scope, but present in views">
                <WarningIcon sx={{ color: 'warning.main', fontSize: 18 }} />
              </Tooltip>
            );
          }
          return null;
        },
      });
    }

    cols.push({
      field: 'data_index',
      headerName: 'data_index',
      flex: 1,
      minWidth: 150,
    });
    cols.push({
      field: 'label',
      headerName: 'label',
      flex: 1,
      minWidth: 150,
      valueGetter: (_v, row) => row.label || row.data_index,
    });

    if (hasAnyCategory) {
      cols.push({
        field: 'category',
        headerName: 'category',
        width: 100,
        valueGetter: (_v, row) => row.category || '',
      });
    }

    cols.push({
      field: 'type',
      headerName: 'type',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.is_attribute ? 'A' : 'M'}
          color={params.row.is_attribute ? 'default' : 'primary'}
          variant="outlined"
        />
      ),
    });

    cols.push({
      field: 'renderer',
      headerName: 'renderer',
      width: 110,
      renderCell: (params) => {
        // Attributes don't carry renderers — leave the cell empty rather
        // than showing a misleading "needs variant" warning.
        if (params.row.is_attribute) {
          return <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>—</Typography>;
        }
        const r = params.row.current_renderer;
        if (r) return <Typography sx={{ fontSize: 13 }}>{r}</Typography>;
        return <Chip size="small" label="needs variant" color="warning" />;
      },
    });

    // Per-view dynamic columns. Header shows the configName (e.g.
    // "nested-sku/HistoryHistoryGridNestedViewConfigureSKU") so the column matches
    // what's in the sidebar leaf. The unique view_key (view_name) is the cell
    // address; the configName is just the label. Tooltip shows the bare
    // view_name + viewLabel + view_type for additional context.
    data.views.forEach((view) => {
      const headerLabel = view.configName || view.view_name;
      cols.push({
        field: view.view_key,
        headerName: headerLabel,
        width: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Tooltip
            title={`${view.view_name} · ${view.view_label} · ${view.view_type || 'no type'}`}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {headerLabel}
            </Typography>
          </Tooltip>
        ),
        renderCell: (params) => {
          const row = params.row;
          const checked = effectivePresence(row, view.view_key);
          const presenceEntry = row.presence?.[view.view_key];
          const drift =
            presenceEntry?.present === true &&
            presenceEntry?.renderer != null &&
            row.current_renderer != null &&
            presenceEntry.renderer !== row.current_renderer;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Checkbox
                size="small"
                checked={checked}
                disabled
                sx={{ p: 0.5 }}
              />
              {drift && (
                <Tooltip
                  title={`Renderer drift: view uses ${presenceEntry?.renderer}, current is ${row.current_renderer}`}
                >
                  <WarningIcon sx={{ color: 'warning.main', fontSize: 14 }} />
                </Tooltip>
              )}
            </Box>
          );
        },
      });
    });

    return cols;
  }, [data, hasAnyCategory, effectivePresence, handleToggle]);


  const prd = data?.prd_summary;

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header strip: section title + close button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {[section, leftNav, viewLabel].filter(Boolean).join(' / ')} — Coverage Matrix
        </Typography>
        {onClose && (
          <Button size="small" onClick={onClose} variant="text">
            Close matrix
          </Button>
        )}
      </Box>

      <Alert severity="info" sx={{ mb: 1.5 }}>
        Coverage is read-only. To change which metrics + attributes appear in this
        section's views, use the <strong>Catalog</strong> sub-tab to import an
        updated spreadsheet.
      </Alert>

      {/* PRD Banner */}
      {prd && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {`AEO catalog imported ${formatImportedDate(prd.imported_at)} — ${prd.in_scope_metric_count} metrics, ${prd.in_scope_attribute_count} attributes in scope.`}</Alert>
      )}

      {/* Conflict Banner */}
      {conflictBanner && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setConflictBanner(null)}>
              Dismiss
            </Button>
          }
        >
          {conflictBanner}
        </Alert>
      )}

      {/* Filter Row */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder={
            searchColumn === 'all'
              ? 'Search data_index, label, or renderer'
              : `Search ${searchColumn.replace('_', ' ')}`
          }
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ width: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Search in</InputLabel>
          <Select
            label="Search in"
            value={searchColumn}
            onChange={(e) => setSearchColumn(e.target.value as typeof searchColumn)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="data_index">Data Index</MenuItem>
            <MenuItem value="label">Data Label</MenuItem>
            <MenuItem value="renderer">Renderer</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="metric">Metric</MenuItem>
            <MenuItem value="attribute">Attribute</MenuItem>
          </Select>
        </FormControl>
        {hasAnyCategory && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select
              multiple
              label="Category"
              value={categoryFilter}
              onChange={(e) => {
                const v = e.target.value;
                setCategoryFilter(typeof v === 'string' ? v.split(',') : (v as string[]));
              }}
              input={<OutlinedInput label="Category" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((s) => (
                    <Chip key={s} size="small" label={s} />
                  ))}
                </Box>
              )}
            >
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  <Checkbox checked={categoryFilter.indexOf(c) > -1} />
                  <ListItemText primary={c} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {prd && (
          <FormControlLabel
            control={
              <Switch
                checked={prdScopeOnly}
                onChange={(e) => setPrdScopeOnly(e.target.checked)}
              />
            }
            label="PRD scope only"
          />
        )}
        <Typography sx={{ fontSize: 13, color: 'text.secondary', ml: 1 }}>
          {data ? `${filteredRows.length} of ${data.rows.length} rows` : ''}
        </Typography>
        <Box sx={{ flex: 1 }} />
      </Box>

      {/* DataGrid */}
      <Box sx={{ flex: 1, minHeight: 400 }}>
        {loading && !data ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
            <CircularProgress />
          </Stack>
        ) : (
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={(r) => r.data_index}
            density="compact"
            disableRowSelectionOnClick
            initialState={{
              pinnedColumns: { left: ['__scope', 'data_index'] },
            } as any}
          />
        )}
      </Box>

      {/* Variant Picker */}
      {pickerQueue[0] && (
        <VariantPickerModal
          open
          dataIndex={pickerQueue[0].dataIndex}
          prdCalculation={pickerQueue[0].prdCalculation}
          onChoose={(choice) => {
            const di = pickerQueue[0].dataIndex;
            setCurrentVariantChoices((prev) => ({ ...prev, [di]: choice }));
            setPickerQueue((prev) => prev.slice(1));
            // If this was the last item AND we were mid-save, continue.
            if (pickerQueue.length === 1 && pendingSaveRef.current) {
              // Use a microtask to ensure state updates have been queued
              Promise.resolve().then(() => handleSaveAfterVariants());
            }
          }}
          onCancel={() => {
            // Drop all adds for this data_index from the diff
            const di = pickerQueue[0].dataIndex;
            setDiff((prev) => {
              const next = new Map(prev);
              const prefix = di + '|';
              for (const k of Array.from(next.keys())) {
                if (k.startsWith(prefix) && next.get(k) === true) next.delete(k);
              }
              return next;
            });
            setPickerQueue((prev) => prev.slice(1));
            pendingSaveRef.current = false;
          }}
        />
      )}
    </Box>
  );
};

export default CoverageMatrix;
